import { Check, ChevronDown } from '@tamagui/lucide-icons';
import { Accordion } from '@unistocks/components/accordion/accordion';
import { Image } from '@unistocks/components/basic/image';
import { Group } from '@unistocks/components/group/group';
import { Row } from '@unistocks/components/layout/flex';
import { FullScreenSheet } from '@unistocks/components/sheet/full-screen-sheet';
import { H3 } from '@unistocks/components/text/text';
import { InstrumentListItem } from '@unistocks/features/external-accounts/components/Instrument-list-item';
import {
  InstrumentSettings,
  InstrumentSettingsFormData
} from '@unistocks/features/external-accounts/components/instrument-settings';
import { renderValidationIcon } from '@unistocks/features/external-accounts/import/utils';
import {
  findMatchingAssetInTargetPortfolio,
  getInstrumentImage,
  getInstrumentSubtitle,
  getSectionValidation
} from '@unistocks/features/external-accounts/utils';
import {
  ExternalAccountDto,
  ExternalPortfolioDto,
  InstrumentDto,
  PortfolioDto
} from '@unistocks/proxy/axios-client';
import { useIsDarkMode } from '@unistocks/shared/hooks/use-active-color-scheme';
import { intersection, moveItemInArray } from '@unistocks/shared/utils/util';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, CheckedState, getVariableValue, Separator, Square } from 'tamagui';

const renderSectionWarning = (
  section: InstrumentDto[],
  targetPortfolio: PortfolioDto
): React.ReactNode => {
  if (!targetPortfolio || section.length === 0) return null;

  return renderValidationIcon(getSectionValidation(section, targetPortfolio));
};

export type ImportExternalPortfoliosProps = {
  targetPortfolio?: PortfolioDto;
  externalAccount: ExternalAccountDto;
  externalPortfolios: ExternalPortfolioDto[];
  selectedForImport: Record<string, CheckedState>;
  setSelectedFormImport: (selected: Record<string, CheckedState>) => void;
  onInstrumentSettingsChange?: (instrument: InstrumentDto) => void;
};

export const ExternalPortfolios: React.FC<ImportExternalPortfoliosProps> = ({
  targetPortfolio,
  externalAccount,
  externalPortfolios,
  selectedForImport,
  setSelectedFormImport,
  onInstrumentSettingsChange
}) => {
  const isDarkMode = useIsDarkMode();
  const [activeInstrument, setActiveInstrument] = useState<InstrumentDto>();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const padding4 = getVariableValue('$4', 'space');
  const activeInstrumentPortfolio = useMemo(
    () =>
      activeInstrument
        ? externalPortfolios.find(({ id }) => id === activeInstrument.portfolioId)
        : undefined,
    [activeInstrument, externalPortfolios]
  );

  useEffect(() => {
    if (!targetPortfolio) return;

    // If the provider was imported before, only preselect the external portfolios that are already imported
    const previouslyImported = intersection(
      targetPortfolio.syncedPortfolios.map(({ id }) => id),
      externalPortfolios.map(({ id }) => id)
    );

    setSelectedFormImport(
      externalPortfolios.reduce<Record<string, CheckedState>>(
        (acc, portfolio) => {
          acc[portfolio.id] = previouslyImported.length
            ? previouslyImported.includes(portfolio.id)
            : true;
          return acc;
        },
        {} as Record<string, CheckedState>
      )
    );
  }, [targetPortfolio, externalPortfolios, setSelectedFormImport]);

  const onInstrumentSetingsSave = useCallback(
    ({ sectionId, isHidden, isManualAsset, selectedInstrument }: InstrumentSettingsFormData) => {
      setSettingsOpen(false);

      if (activeInstrument) {
        const updatedInstrument = { ...activeInstrument };

        updatedInstrument.importSettings = {
          ...activeInstrument.importSettings,
          sectionId,
          isHidden,
          isManualAsset
        };

        const prevIndex = updatedInstrument.searchResult.findIndex(
          x =>
            x.dataSource === selectedInstrument?.dataSource &&
            x.identifier === selectedInstrument?.identifier
        );

        if (prevIndex > 0) {
          // User selected another asset from the existing search result
          moveItemInArray(updatedInstrument.searchResult, prevIndex, 0);
          updatedInstrument.importSettings.hasSelectedListedAsset = true;
        } else if (prevIndex === -1 && selectedInstrument) {
          // User searched and selected a new asset
          updatedInstrument.searchResult.unshift(selectedInstrument);
          updatedInstrument.importSettings.hasSelectedListedAsset = true;
        }

        onInstrumentSettingsChange?.(updatedInstrument);
      }

      setActiveInstrument(undefined);
    },
    [activeInstrument, onInstrumentSettingsChange]
  );

  const previouslyImported = activeInstrumentPortfolio
    ? targetPortfolio
      ? intersection(
          targetPortfolio.syncedPortfolios.map(x => x.id),
          externalPortfolios.map(x => x.id)
        ).includes(activeInstrumentPortfolio.id)
      : false
    : false;

  return (
    <>
      {targetPortfolio ? (
        <Accordion type={'multiple'}>
          <Group separator={<Separator />}>
            {externalPortfolios.map((portfolio, index) => {
              const isSelected = selectedForImport[portfolio.id];
              return (
                <Group.Item key={portfolio.id}>
                  <Accordion.Item value={portfolio.id}>
                    <Accordion.Trigger style={{ paddingVertical: padding4 }}>
                      {({ open }) => (
                        <Row gap={'$3'} alignItems={'center'}>
                          <Image rounded size={'$2.5'} source={externalAccount.provider.imageUri} />
                          <H3 color={'$headingFont'} size={'$7'} flex={1} userSelect="none">
                            {portfolio.name}
                          </H3>
                          {isSelected
                            ? renderSectionWarning(portfolio.instruments, targetPortfolio)
                            : null}
                          <Checkbox
                            name={`selected-for-import-${index}`}
                            id={portfolio.id}
                            checked={isSelected}
                            onCheckedChange={checked =>
                              setSelectedFormImport({
                                ...selectedForImport,
                                [portfolio.id]: checked
                              })
                            }
                            size={'$5'}
                            ml={'auto'}
                          >
                            <Checkbox.Indicator>
                              <Check />
                            </Checkbox.Indicator>
                          </Checkbox>
                          <Square transition={'quick'} rotate={open ? '180deg' : '0deg'}>
                            <ChevronDown size={'$2'} />
                          </Square>
                        </Row>
                      )}
                    </Accordion.Trigger>

                    <Accordion.Content>
                      <Group separator={<Separator />}>
                        {portfolio.instruments.map(instrument => (
                          <Group.Item key={instrument.id}>
                            <InstrumentListItem
                              size={'$5'}
                              $md={{ size: '$4' }}
                              instrument={instrument}
                              targetPortfolio={targetPortfolio}
                              disabled={!isSelected}
                              onPress={() => {
                                setActiveInstrument(instrument);
                                setSettingsOpen(true);
                              }}
                            />
                          </Group.Item>
                        ))}
                      </Group>
                    </Accordion.Content>
                  </Accordion.Item>
                </Group.Item>
              );
            })}
          </Group>
        </Accordion>
      ) : null}

      <FullScreenSheet
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        headerTitle={activeInstrument?.name}
        headerSubtitle={activeInstrument ? getInstrumentSubtitle(activeInstrument) : undefined}
        headerIcon={
          activeInstrument ? (
            <Image
              rounded
              size={'$2.5'}
              source={getInstrumentImage(activeInstrument, isDarkMode)}
            />
          ) : null
        }
        contentTopMargin
      >
        {activeInstrument && activeInstrumentPortfolio && targetPortfolio ? (
          <InstrumentSettings
            instrument={activeInstrument}
            matchingAsset={findMatchingAssetInTargetPortfolio(activeInstrument, targetPortfolio)}
            targetPortfolio={targetPortfolio}
            externalPortfolio={activeInstrumentPortfolio}
            externalAccount={externalAccount}
            previouslyImported={previouslyImported}
            onSave={onInstrumentSetingsSave}
          />
        ) : null}
      </FullScreenSheet>
    </>
  );
};

ExternalPortfolios.displayName = 'ExternalPortfolios';
